import { useState, useEffect, useRef, useContext } from 'react';
import { Button, Dropdown } from 'semantic-ui-react';
import { changeDocTitle, getThisSemesterRangeOrDates, scrollUp } from 'src/components/helpers';
import ReportButton from 'src/containers/admin/ReportButton';
import moment from 'moment';
import crecerFormsInstance from 'src/services/api/crecer';
import AGTable from 'src/components/table/aggrid';
import { getRelationsTableColumns } from './RelationsTableColumns';
import styled from '@emotion/styled';
import Swal from 'sweetalert2';
import RelationsGuestAnswers from './RelationsGuestAnswers';
import { ListsContext } from 'src/services/contextProviders/lists/listsContext';
import RelationsLimitsModal from './RelationsLimitsModal';
import { screenSizes } from 'src/assets/styles/Literals';

const AdminTableContainer = styled.section`
  background-color: white;
  padding: 20px 40px;
  border-radius: 10px;
  min-width: 100vw;
  margin: 0 auto;

  @media (min-width: ${screenSizes.laptop}) {
    min-width: 90vw;
  }

  @media (max-width: ${screenSizes.tablet}) {
    min-width: 95vw;
    padding: 10px;
  }
`;

const AdminTableContent = styled.div`
  margin-bottom: 20px;

  > div:first-child {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;

    @media (max-width: ${screenSizes.tablet}) {
      flex-direction: column;
      gap: 15px;
      align-items: stretch;
    }
  }
`;

const LoadingContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: #6c757d;
  font-size: 16px;
`;

const EmptyContainer = styled.div`
  padding: 40px;
  text-align: center;
  color: #6c757d;
  font-size: 16px;
`;

const SummaryContainer = styled.div`
  display: flex;
  justify-content: space-around;
  margin-top: 20px;
  padding: 15px;
  background: #e9ecef;
  border-radius: 8px;
  text-align: center;
  font-weight: 600;
  color: #495057;

  @media (max-width: ${screenSizes.tablet}) {
    flex-direction: column;
    gap: 15px;
    padding: 12px;
  }

  @media (max-width: ${screenSizes.mobile}) {
    padding: 10px;
    gap: 12px;
    font-size: 14px;
  }
`;

const SemesterSelectorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 20px 0 0 0;

  .semester-label {
    font-weight: 600;
    color: #495057;
    min-width: 120px;
  }

  .semester-dropdown,
  .year-dropdown {
    min-width: 200px;
  }

  @media (max-width: ${screenSizes.tablet}) {
    flex-direction: column;
    align-items: stretch;
    gap: 15px;
    padding: 15px 0 0 0;

    .semester-label {
      min-width: auto;
      margin-bottom: 5px;
    }

    .semester-dropdown,
    .year-dropdown {
      min-width: auto;
      width: 100%;
    }
  }

  @media (max-width: ${screenSizes.mobile}) {
    gap: 12px;
    padding: 10px 0 0 0;
  }
`;

const { from_date, to_date } = getThisSemesterRangeOrDates();

const RelationsTable = (props) => {
  const [loading, set_loading] = useState(true);
  const [inputs, setInputs] = useState({});
  const [relationsData, set_relationsData] = useState([]);
  const [selectedSemester, setSelectedSemester] = useState(() => {
    const currentMonth = moment().format('M');
    return currentMonth >= 7 ? 'second' : 'first';
  });
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [handleDate, set_handleDate] = useState({
    startDate: from_date,
    endDate: to_date,
  });
  const [showDetails, setShowDetails] = useState(false);
  const [selectedRelation, setSelectedRelation] = useState(null);
  const [showLimitsModal, setShowLimitsModal] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const { data } = useContext(ListsContext);
  const gridRef = useRef(null);
  const totalWomen = data?.find(
    (item) => item.name === 'relations_limit' && item.conditional === 'Femenino'
  );
  const totalMen = data?.find(
    (item) => item.name === 'relations_limit' && item.conditional === 'Masculino'
  );

  const currentLimits = {
    male: totalMen,
    female: totalWomen,
  };

  const filteredRelationsData = relationsData.filter((item) =>
    showWaitlist ? !!item.waitlist : !item.waitlist
  );

  const getSemesterDates = (semester, year) => {
    if (semester === 'first') {
      return {
        startDate: moment().year(year).startOf('year'),
        endDate: moment()
          .year(year)
          .startOf('year')
          .add(6, 'months')
          .subtract(1, 'day')
          .endOf('day'),
      };
    } else {
      return {
        startDate: moment().year(year).startOf('year').add(6, 'months'),
        endDate: moment().year(year).endOf('year'),
      };
    }
  };

  const semesterOptions = [
    { key: 'first', text: 'Primer Semestre (Enero - Junio)', value: 'first' },
    { key: 'second', text: 'Segundo Semestre (Julio - Diciembre)', value: 'second' },
  ];

  const generateYearOptions = () => {
    const currentYear = moment().year();
    const years = [];
    // Generate options for past 5 years, current year, and next 10 years
    for (let i = 5; i >= 0; i--) {
      const year = currentYear - i;
      years.push({ key: year, text: year.toString(), value: year });
    }
    return years;
  };

  const handleSemesterChange = (semester) => {
    setSelectedSemester(semester);
    const dates = getSemesterDates(semester, selectedYear);
    set_handleDate(dates);
    setInputs((prev) => ({
      ...prev,
      startDate: dates.startDate.startOf('day')._d.getTime(),
      endDate: dates.endDate.endOf('day')._d.getTime(),
    }));
  };

  const handleYearChange = (year) => {
    setSelectedYear(year);
    const dates = getSemesterDates(selectedSemester, year);
    set_handleDate(dates);
    setInputs((prev) => ({
      ...prev,
      startDate: dates.startDate.startOf('day')._d.getTime(),
      endDate: dates.endDate.endOf('day')._d.getTime(),
    }));
  };

  useEffect(() => {
    changeDocTitle('Inscripciones Seminario Relaciones Sanas');
    scrollUp();
    getAllRelationsForm();
  }, [handleDate.endDate]);

  const getAllRelationsForm = () => {
    set_loading(true);
    let body = {
      ...inputs,
      startDate: handleDate.startDate ? handleDate.startDate.startOf('day')._d.getTime() : null,
      endDate: handleDate.endDate ? handleDate.endDate.endOf('day')._d.getTime() : null,
    };

    crecerFormsInstance
      .getAllRelationsForm(body)
      .then((response) => {
        if (response.data.success) {
          set_relationsData(response.data.Answers);
        }
      })
      .catch((err) => {
        console.error('Error fetching relations data:', err);
        Swal.fire({
          title: 'Error',
          text: err.response.data.message || 'Ocurrió un error al obtener los datos',
          icon: 'error',
        });
      })
      .finally(() => {
        set_loading(false);
      });
  };

  const handleViewDetails = (relation) => {
    setSelectedRelation(relation);
    setShowDetails(true);
  };

  const onGridReady = (params) => {
    if (gridRef.current) {
      gridRef.current.api = params.api;
      gridRef.current.columnApi = params.columnApi;
      if (window.innerWidth > screenSizes.tablet) {
        params.api.sizeColumnsToFit();
      }
    }
  };

  return (
    <AdminTableContainer>
      <AdminTableContent>
        <div>
          <ReportButton
            URL={`/crecer/RelationsFormReport`}
            fileName={`reporte_inscripciones_relaciones_sanas.xlsx`}
            event="Reporte Inscripciones Relaciones Sanas"
            filters={handleDate}
          />
        </div>

        <SemesterSelectorContainer>
          <div className="semester-label">Semestre:</div>
          <Dropdown
            className="semester-dropdown"
            placeholder="Seleccionar semestre"
            fluid
            selection
            options={semesterOptions}
            value={selectedSemester}
            onChange={(e, { value }) => handleSemesterChange(value)}
          />
          <div className="semester-label">Año:</div>
          <Dropdown
            className="year-dropdown"
            placeholder="Seleccionar año"
            fluid
            selection
            options={generateYearOptions()}
            value={selectedYear}
            onChange={(e, { value }) => handleYearChange(value)}
          />
        </SemesterSelectorContainer>
      </AdminTableContent>

      <div>
        {loading ? (
          <LoadingContainer>
            <div>Cargando...</div>
          </LoadingContainer>
        ) : (
          <div style={{ height: '70vh', width: '100%' }}>
            <SummaryContainer>
              <div>
                <div>
                  Total hombres inscritos:{' '}
                  {
                    relationsData?.filter(
                      (item) => item.Guest.gender === 'Masculino' && !item.waitlist
                    ).length
                  }
                </div>
                <div>
                  Lista de espera hombres:{' '}
                  {
                    relationsData?.filter(
                      (item) => item.Guest.gender === 'Masculino' && item.waitlist
                    ).length
                  }
                </div>
                <div>Limite hombres: {totalMen?.key || 0}</div>
              </div>
              <div>
                <div>
                  Total mujeres inscritas:{' '}
                  {
                    relationsData?.filter(
                      (item) => item.Guest.gender === 'Femenino' && !item.waitlist
                    ).length
                  }
                </div>
                <div>
                  Lista de espera mujeres:{' '}
                  {
                    relationsData?.filter(
                      (item) => item.Guest.gender === 'Femenino' && item.waitlist
                    ).length
                  }
                </div>
                <div>Limite mujeres: {totalWomen?.key || 0}</div>
              </div>
              <div>
                <Button onClick={() => setShowLimitsModal(true)}>Cambiar Limites</Button>
              </div>
            </SummaryContainer>
            <div style={{ margin: '12px 0' }}>
              <Button
                color={showWaitlist ? 'orange' : 'blue'}
                onClick={() => setShowWaitlist((prev) => !prev)}
              >
                {showWaitlist ? 'Ver confirmados' : 'Ver lista de espera'}
              </Button>
            </div>
            {filteredRelationsData.length === 0 ? (
              <EmptyContainer>
                <div>
                  {showWaitlist ? 'No hay personas en lista de espera' : 'No hay inscripciones'}
                </div>
              </EmptyContainer>
            ) : (
              <AGTable
                columnDefs={getRelationsTableColumns(handleViewDetails)}
                rowData={filteredRelationsData}
                tableRef={gridRef}
                onGridReady={onGridReady}
                paginationPageSize={20}
                suppressDragLeaveHidesColumns={false}
                rowHeight={60}
              />
            )}
          </div>
        )}
      </div>
      {showDetails && (
        <RelationsGuestAnswers
          showDetails={showDetails}
          setShowDetails={setShowDetails}
          selectedRelation={selectedRelation}
          getAllRelationsForm={getAllRelationsForm}
        />
      )}
      {showLimitsModal && (
        <RelationsLimitsModal
          open={showLimitsModal}
          onClose={() => setShowLimitsModal(false)}
          currentLimits={currentLimits}
        />
      )}
    </AdminTableContainer>
  );
};

export default RelationsTable;
